import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Row,
  TableInstance,
  TableOptions,
  useExpanded,
  UseExpandedInstanceProps,
  useFlexLayout,
  useGroupBy,
  UseGroupByInstanceProps,
  useResizeColumns,
  UseResizeColumnsState,
  useTable,
} from 'react-table';

import { usePermissions } from '@mint-lib/profile-helpers';
import { isEmpty } from 'remeda';

import { BUILDER_ROW_TYPES } from '../../../constants/campaignBuilder.js';
import { Columns } from '../../../constants/myCampaigns.js';
import {
  VERSION_ROW_TYPES,
  VersionsColumnsObjectValue,
} from '../../../constants/versionsColumnsObject.js';
import { useCampaignDataSelector } from '../../../hooks/useGetCampaignData.js';
import useGetPlatformIntegrations from '../../../hooks/useGetPlatformIntegrations.js';
import { useGetVersionListForTableView } from '../../../hooks/useGetVersionListForTableView.js';
import {
  VersionsTableRow,
  VersionTableColumn,
} from '../../../types/versions.js';
import {
  getVersionsColumnObjects,
  prepareMediaPlanVersionRows,
} from '../../../utils/mediaPlanVersions.js';

type TableInstanceWithHooks<T extends object> = TableInstance<T> &
  UseGroupByInstanceProps<T> &
  UseExpandedInstanceProps<T> & {
    state: UseResizeColumnsState<T>;
    rows: VersionsTableRow[] & Row<{}>[];
    column: VersionTableColumn;
    flatRows: VersionsTableRow[];
    expandedRows: VersionsTableRow[];
  };

type TableOptionsWithHooks<T extends object> = TableOptions<T> & {
  autoResetExpanded: boolean;
};

const useVersionsTable = (sortedVersions?: { id: number }[] | null) => {
  const initialExpanded = useRef<boolean | null>(null);
  const expandedBy = useRef<VERSION_ROW_TYPES | null>(null);

  const { data: currencySign } = useCampaignDataSelector('currency.sign');
  const { integratedPlatforms } = useGetPlatformIntegrations();

  const {
    data: { versions: versionsData, fieldsMeta } = {},
    isLoading: isVersionsLoading,
  } = useGetVersionListForTableView();

  const [expandedType, setExpandedType] = useState(VERSION_ROW_TYPES.PLATFORM);

  const { names: columnsNames = [], objects: columnsObjects = [] } =
    useMemo(() => {
      if (!fieldsMeta) {
        return {};
      }

      const basicColumns = [
        { name: Columns.EXPANDER, label: '' },
        ...fieldsMeta,
        { name: Columns.ACTIONS_MENU, label: '' },
      ];

      const names = basicColumns.map((column) => column.name);
      const objects = getVersionsColumnObjects(basicColumns);

      return { names, objects };
    }, [fieldsMeta]);

  const preparedRows = useMemo(
    () =>
      prepareMediaPlanVersionRows({
        versions: versionsData,
        platformsIntegrations: integratedPlatforms,
        currencySign,
      }),
    [versionsData, integratedPlatforms, currencySign],
  );

  const { permissions } = usePermissions();
  const isNonFinancialUser = !permissions.financialDataFeature;

  const sortedRows = useMemo(() => {
    return preparedRows.filter((versionRow) =>
      sortedVersions?.some(
        (sortedVersion) =>
          Number(sortedVersion.id) === Number(versionRow.versionId),
      ),
    );
  }, [sortedVersions, preparedRows]);

  const preparedColumns = useMemo(
    () =>
      columnsObjects?.reduce((acc, column) => {
        if (isNonFinancialUser && column.hideColumnForNonFinancialUser) {
          return acc;
        }
        if (column.id === Columns.EXPANDER) {
          const [firstRow, ...otherRows] = preparedRows || [];
          const isInitialOpened = !otherRows.length && isEmpty(firstRow);

          return [
            ...acc,
            {
              ...column,
              isInitialOpened,
            },
          ];
        }

        return [
          ...acc,
          {
            ...column,
          },
        ];
      }, [] as VersionsColumnsObjectValue[]),
    [preparedRows],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    columns,
    prepareRow,
    toggleRowExpanded,
    toggleAllRowsExpanded,
    flatRows,
    expandedRows,
    state: { columnResizing },
  } = useTable(
    {
      columns: preparedColumns as unknown,
      data: sortedRows,
      initialState: {
        columnOrder: columnsNames,
      },
      autoResetExpanded: false,
    } as TableOptionsWithHooks<{}>,
    useGroupBy,
    useExpanded,
    useFlexLayout,
    useResizeColumns,
  ) as TableInstanceWithHooks<{}>;

  const expandRowsByType = useCallback(
    (type: VERSION_ROW_TYPES) => {
      expandedBy.current = type;
      if (type === VERSION_ROW_TYPES.PLATFORM) {
        toggleAllRowsExpanded(true);
      }

      if (type === VERSION_ROW_TYPES.VERSION) {
        toggleAllRowsExpanded(false);
      }

      if (type === VERSION_ROW_TYPES.CHANNEL) {
        flatRows.forEach((row: VersionsTableRow) => {
          if (typeof row.id === 'string') {
            row.original.type === BUILDER_ROW_TYPES.VERSION
              ? toggleRowExpanded([row.id], true)
              : toggleRowExpanded([row.id], false);
          }
        });
      }
    },
    [flatRows, toggleAllRowsExpanded, toggleRowExpanded],
  );

  useEffect(() => {
    const channels = flatRows.filter(
      (row: VersionsTableRow) =>
        row.original.type === BUILDER_ROW_TYPES.CHANNEL,
    );

    const expandedChanels = expandedRows.filter(
      (row: VersionsTableRow) =>
        row.original.type === BUILDER_ROW_TYPES.CHANNEL,
    );

    const versions = flatRows.filter(
      (row: VersionsTableRow) =>
        row.original.type === BUILDER_ROW_TYPES.VERSION,
    );

    const expandedVersions = expandedRows.filter(
      (row: VersionsTableRow) =>
        row.original.type === BUILDER_ROW_TYPES.VERSION,
    );

    const isAllRowsExpanded = expandedRows.length === flatRows.length;
    const isAllVersionsExpanded =
      expandedRows.length === versions.length && !versions.length;
    const isAnyRowExpanded = expandedRows.some(
      (row: VersionsTableRow) =>
        row.original.type === BUILDER_ROW_TYPES.PLATFORM,
    );
    const isAllVersionsAndChannelsExpanded =
      channels.length === expandedChanels.length &&
      versions.length === expandedVersions.length &&
      !isAnyRowExpanded &&
      !isAllRowsExpanded;
    if (isAllRowsExpanded) {
      setExpandedType(VERSION_ROW_TYPES.PLATFORM);
    }

    if (isAllVersionsAndChannelsExpanded) {
      setExpandedType(VERSION_ROW_TYPES.CHANNEL);
    }

    if (isAllVersionsExpanded) {
      setExpandedType(VERSION_ROW_TYPES.VERSION);
    }
  }, [expandedRows, flatRows]);

  useEffect(() => {
    if (flatRows.length && !initialExpanded.current) {
      setExpandedType(VERSION_ROW_TYPES.PLATFORM);
      expandRowsByType(VERSION_ROW_TYPES.PLATFORM);
      initialExpanded.current = true;
    }
  }, [flatRows.length]);

  useEffect(() => {
    if (expandedBy.current !== expandedType) {
      expandRowsByType(expandedType);
    }
  }, [expandedType]);

  return useMemo(
    () => ({
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow,
      toggleRowExpanded,
      columns,
      expandedType,
      columnResizing,
      setExpandedType,
      isVersionsLoading,
    }),
    [
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow,
      toggleRowExpanded,
      columns,
      expandedType,
      columnResizing,
      setExpandedType,
    ],
  );
};

export default useVersionsTable;
